import { Component, OnInit, inject, input, signal } from '@angular/core';
import { AccessGroupIdTag, IdTag } from '../id-tag-model';
import { combineLatest, filter, firstValueFrom, map, of, startWith, switchMap, tap } from 'rxjs';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { IdTagTypeEnum } from 'src/app/shared/enums/id-tag-type.enum';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { Router, RouterModule } from '@angular/router';
import { IdTagService } from 'src/app/shared/services/id-tag.service';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { EmptyDashPipe } from 'src/app/shared/pipes/empty-dash.pipe';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AccessGroupUsersSearchEvent } from '../../charging-users/charging-user.model';
import { PageEvent } from '@angular/material/paginator';
import { IdTagListStateService } from '../id-tags-overview/id-tag-list.state.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'adm-id-tag-details',
  templateUrl: './id-tag-details.component.html',
  styleUrls: ['./id-tag-details.component.scss'],
  imports: [CommonModule, RouterModule, MatIconModule, MatPaginatorModule, MatTooltipModule, DkCurrencyPipe, EmptyDashPipe],
})
export class IdTagDetailsComponent {
  departmentService = inject(DepartmentService);
  chargingUserService = inject(ChargingUserService);
  state = inject(IdTagListStateService);
  private router = inject(Router);
  private idTagService = inject(IdTagService);

  idTag = signal<IdTag | null>(null);

  idTagId = input.required<number>();
  public address = '';
  public chargingUserName = '';
  public addressInfo = 'Alternativ adresse';
  public departmentAgreementNumber: number | null = null;
  public idTagTypeEnum = IdTagTypeEnum;
  public hasCompanyCar = false;
  public idTagUserType: typeof IdTagUserType = IdTagUserType;
  public discountType: typeof DiscountType = DiscountType;

  public get agreementNumberForDisplay(): number | null {
    const paymentInfo = this.idTag()?.paymentInfo;
    if (paymentInfo && paymentInfo > 0) {
      return paymentInfo;
    }

    const agreementNumber = this.idTag()?.agreementNumber;
    if (agreementNumber && agreementNumber > 0) {
      return agreementNumber;
    }

    if (this.departmentAgreementNumber && this.departmentAgreementNumber > 0) {
      return this.departmentAgreementNumber;
    }

    return null;
  }

  constructor() {
    toObservable(this.idTagId)
      .pipe(
        filter(id => id > 0),
        switchMap(id => this.idTagService.getIdTag(id)),
        tap(idTag => {
          this.idTag.set(idTag);
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    toObservable(this.idTag)
      .pipe(
        filter((idTag): idTag is IdTag => idTag !== null),
        tap(idTag => {
          this.address = idTag.shippingAddress;

          if (idTag.chargingUserId > 0) {
            this.chargingUserService.getChargingUser(idTag.chargingUserId).subscribe({
              next: async chargingUser => {
                if (chargingUser) {
                  this.hasCompanyCar = chargingUser.idTagUserType.toString() === IdTagUserType[this.idTagUserType.CompanyCar].toString();
                  this.chargingUserName = chargingUser.name;
                }
              },
              error: () => {
                return; //only purpose is to prevent generic error handling kicking in for a possible 404
              },
            });
          }
        }),
        switchMap(idTag => {
          if (idTag.departmentId > 0) {
            return this.departmentService.getDepartment(idTag.departmentId).pipe(
              tap(idTagDepartment => {
                this.departmentAgreementNumber = idTagDepartment?.agreementNumber ?? null;

                if (idTagDepartment && (this.address === idTagDepartment.address || this.address?.length === 0)) {
                  this.address = idTagDepartment.address;
                  this.addressInfo = 'Afdelingens adresse';
                }

                if (!this.address) {
                  this.address = 'Der kunne hverken findes adresse på brik eller på afdeling';
                }
              })
            );
          } else {
            this.departmentAgreementNumber = null;

            if (!this.address) {
              this.address = 'Der kunne hverken findes adresse på brik eller på afdeling';
            }
            return of(null);
          }
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  goToChargingUser(chargingUserId: number) {
    this.router.navigateByUrl('/charging-users?chargingUserId=' + chargingUserId);
  }

  accessGroups$ = combineLatest([toObservable(this.idTag), this.state.refreshIdTagSubject.pipe(startWith(null))]).pipe(
    filter((value): value is [IdTag, IdTag | null] => value[0] !== null && (!value[1] || value[1].id === value[0].id)),
    switchMap(([idTag, _]) => {
      if (!idTag.chargingUserId) {
        return this.idTagService.getAccessGroups(idTag.id).pipe(map(x => x.items));
      } else {
        return this.chargingUserService.getChargingUser(idTag.chargingUserId).pipe(
          switchMap(chargingUser => {
            const pageEvent = new PageEvent();
            pageEvent.pageSize = 100;
            const searchEvent = new AccessGroupUsersSearchEvent(pageEvent);
            searchEvent.chargingUserIdentifier = chargingUser.identifier;
            return this.chargingUserService.getAccessGroupUsers(searchEvent).pipe(
              map(res => {
                return res.items.map(
                  ag =>
                    ({
                      accessGroupIdentifier: ag.identifier,
                      idTagId: null,
                      name: ag.name,
                      amount: ag.amount,
                      discountType: ag.discountType,
                      deleted: '',
                    }) as AccessGroupIdTag
                );
              })
            );
          })
        );
      }
    }),
    takeUntilDestroyed()
  );

  idTagRefresh$ = this.state.refreshIdTagSubject
    .pipe(startWith(null))
    .pipe(
      tap(refreshIdTag => {}),
      filter(
        (refreshIdTag): refreshIdTag is IdTag => refreshIdTag !== null && this.idTag() !== null && refreshIdTag.id === this.idTag()!.id
      ),
      switchMap(refreshIdTagSubject => {
        return this.idTagService.getIdTag(refreshIdTagSubject.id).pipe(
          tap(updatedIdTag => {
            this.idTag.set(updatedIdTag);
          })
        );
      }),
      takeUntilDestroyed()
    )
    .subscribe();
}
